import { useEffect, useState } from 'react'
import { client } from '../../lib/sanity'
import Modal from '../ui/Modal'
import Input from '../ui/Input'
import Button from '../ui/Button'

const HEX_RE = /^#([0-9A-Fa-f]{6})$/
const randomColor = () => '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0')

export default function CategoryManagerModal({ open, onClose, categories, onChanged }) {
    const [rows, setRows] = useState(categories)
    const [newTitle, setNewTitle] = useState('')
    const [newColor, setNewColor] = useState(randomColor())

    useEffect(() => {
        if (open) setRows(categories)
    }, [open, categories])

    const updateRow = (id, patch) => {
        setRows(rows.map(r => (r._id === id ? { ...r, ...patch } : r)))
    }

    const commitField = async (id, field, value) => {
        if (field === 'color' && !HEX_RE.test(value)) return
        await client.patch(id).set({ [field]: value }).commit()
        onChanged()
    }

    const setMilestone = async (id) => {
        setRows(rows.map(r => ({ ...r, isMilestone: r._id === id })))
        const others = rows.filter(r => r._id !== id && r.isMilestone)
        await Promise.all([
            client.patch(id).set({ isMilestone: true }).commit(),
            ...others.map(r => client.patch(r._id).set({ isMilestone: false }).commit()),
        ])
        onChanged()
    }

    const deleteCategory = async (id) => {
        setRows(rows.filter(r => r._id !== id))
        await client.delete(id)
        onChanged()
    }

    const addCategory = async () => {
        if (!newTitle.trim() || !HEX_RE.test(newColor)) return
        await client.create({ _type: 'eventCategory', title: newTitle.trim(), color: newColor, isMilestone: false })
        setNewTitle('')
        setNewColor(randomColor())
        onChanged()
    }

    return (
        <Modal open={open} onClose={onClose} title="Manage Categories" maxWidth="max-w-lg">
            <div className="p-4 space-y-3">
                {rows.map(row => (
                    <div key={row._id} className="flex items-center gap-2">
                        <input
                            type="color"
                            value={HEX_RE.test(row.color) ? row.color : '#000000'}
                            onChange={e => {
                                updateRow(row._id, { color: e.target.value })
                                commitField(row._id, 'color', e.target.value)
                            }}
                            className="h-9 w-9 shrink-0 cursor-pointer border-2 border-black dark:border-white bg-transparent p-0"
                            aria-label={`${row.title} color`}
                        />
                        <Input
                            value={row.title}
                            onChange={e => updateRow(row._id, { title: e.target.value })}
                            onBlur={e => commitField(row._id, 'title', e.target.value)}
                            className="flex-1"
                        />
                        <label className="flex shrink-0 items-center gap-1.5 font-mono text-xs uppercase text-black dark:text-white">
                            <input
                                type="checkbox"
                                checked={!!row.isMilestone}
                                onChange={() => setMilestone(row._id)}
                                className="h-4 w-4 cursor-pointer accent-accent"
                            />
                            Milestone
                        </label>
                        <Button variant="destructive" confirm confirmLabel="Confirm" onClick={() => deleteCategory(row._id)}>
                            Delete
                        </Button>
                    </div>
                ))}

                <div className="flex items-center gap-2 border-t-2 border-black dark:border-white pt-3">
                    <input
                        type="color"
                        value={newColor}
                        onChange={e => setNewColor(e.target.value)}
                        className="h-9 w-9 shrink-0 cursor-pointer border-2 border-black dark:border-white bg-transparent p-0"
                        aria-label="New category color"
                    />
                    <Input
                        placeholder="New category name"
                        value={newTitle}
                        onChange={e => setNewTitle(e.target.value)}
                        className="flex-1"
                    />
                    <Button onClick={addCategory}>Add</Button>
                </div>
            </div>
        </Modal>
    )
}
